﻿/**
 * analytics.js
 * Unified Analytics & Error Monitoring Service for Tezer UI Layer.
 * Integrates PostHog (Product Analytics & Opt-in Session Replays)
 * and Sentry (Error Tracking & Performance Monitoring).
 */

import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_ENV = import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? 'development' : 'production');

const OPT_IN_STORAGE_KEY = 'tezer_telemetry_opt_in';

/**
 * Check if the user has opted into optional diagnostics and session recording.
 * Default is FALSE to respect user privacy.
 */
export function isTelemetryOptedIn() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(OPT_IN_STORAGE_KEY) === 'true';
}

/**
 * Update the user's opt-in preference for optional analytics and session recording.
 * Dynamically toggles PostHog session recording.
 */
export function setTelemetryOptIn(optedIn) {
  if (typeof localStorage === 'undefined') return;
  const isEnabled = Boolean(optedIn);
  localStorage.setItem(OPT_IN_STORAGE_KEY, isEnabled ? 'true' : 'false');

  try {
    if (POSTHOG_KEY && typeof posthog.startSessionRecording === 'function') {
      if (isEnabled) {
        posthog.startSessionRecording();
      } else {
        posthog.stopSessionRecording();
      }
    }
  } catch (err) {
    console.warn('[Analytics] Error toggling session recording:', err);
  }

  trackEvent('telemetry_opt_in_changed', { opted_in: isEnabled });
}

/**
 * Initialize PostHog and Sentry before React mounts.
 */
export function initAnalytics() {
  // 1. Initialize Sentry
  if (SENTRY_DSN) {
    try {
      Sentry.init({
        dsn: SENTRY_DSN,
        environment: APP_ENV,
        integrations: [
          Sentry.browserTracingIntegration(),
        ],
        tracesSampleRate: APP_ENV === 'production' ? 0.2 : 1.0,
      });
      console.log('[Sentry] Frontend error monitoring initialized successfully.');
    } catch (sentryErr) {
      console.warn('[Sentry] Failed initializing Sentry:', sentryErr);
    }
  }

  // 2. Initialize PostHog
  if (POSTHOG_KEY) {
    try {
      const optedIn = isTelemetryOptedIn();
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        disable_session_recording: !optedIn,
        session_recording: {
          maskAllInputs: false,
        },
        loaded: (ph) => {
          if (optedIn) {
            ph.startSessionRecording();
          } else {
            ph.stopSessionRecording();
          }
        }
      });
      console.log('[PostHog] Product analytics initialized successfully.');
    } catch (phErr) {
      console.warn('[PostHog] Failed initializing PostHog:', phErr);
    }
  }
}

/**
 * Identify the logged in user across PostHog and Sentry.
 */
export function identifyUser(user) {
  if (!user) return;
  const userId = user.id || user.user_id;
  if (!userId) return;

  const userData = {
    email: user.email,
    display_name: user.display_name || user.name || '',
    country: user.country || '',
    primary_currency: user.primary_currency || ''
  };

  if (POSTHOG_KEY) {
    try {
      posthog.identify(String(userId), userData);
    } catch (e) {
      console.warn('[PostHog] Identify error:', e);
    }
  }

  if (SENTRY_DSN) {
    try {
      Sentry.setUser({
        id: String(userId),
        email: user.email,
        username: user.display_name || user.name
      });
    } catch (e) {
      console.warn('[Sentry] SetUser error:', e);
    }
  }
}

/**
 * Clear user context on logout.
 */
export function resetUser() {
  if (POSTHOG_KEY) {
    try {
      posthog.reset();
    } catch (e) {
      console.warn('[PostHog] Reset error:', e);
    }
  }

  if (SENTRY_DSN) {
    try {
      Sentry.setUser(null);
    } catch (e) {
      console.warn('[Sentry] SetUser(null) error:', e);
    }
  }
}

/**
 * Track a custom event in PostHog and record a breadcrumb in Sentry.
 */
export function trackEvent(eventName, properties = {}) {
  try {
    if (POSTHOG_KEY) {
      posthog.capture(eventName, {
        timestamp: new Date().toISOString(),
        ...properties
      });
    }

    if (SENTRY_DSN) {
      Sentry.addBreadcrumb({
        category: 'user_action',
        message: eventName,
        data: properties,
        level: 'info'
      });
    }
  } catch (err) {
    console.warn('[Analytics] Track error:', eventName, err);
  }
}

/**
 * Capture an exception in Sentry with contextual metadata.
 */
export function captureException(error, context = {}) {
  try {
    console.error('[Error Caught]:', error, context);
    if (SENTRY_DSN) {
      Sentry.captureException(error, context);
    }
  } catch (err) {
    console.warn('[Sentry] CaptureException error:', err);
  }
}

/**
 * Capture an informational or warning message in Sentry.
 */
export function captureMessage(message, level = 'info') {
  try {
    if (SENTRY_DSN) {
      Sentry.captureMessage(message, level);
    }
  } catch (err) {
    console.warn('[Sentry] CaptureMessage error:', err);
  }
}

export { posthog, Sentry };
